// The generator, its pool and the Generate button (DEVELOPMENT.md: Generator, pool and daily
// puzzle). Categories, admin settings and the api's request and response types.
import type { PuzzleDoc } from './puzzle';

export interface GeneratorCategory {
  key: string;
  name: string;
  size: number;
  irregular: boolean;
  /** Constraint types the generator may place; none for classics. */
  types: string[];
  /** A fog puzzle, solved through its fog. */
  fog?: boolean;
}

const MIXED_TYPES = [
  'killer',
  'thermo',
  'arrow',
  'difference',
  'ratio',
  'renban',
  'whispers',
  'palindrome',
  'between',
  'edge-sum',
  'odd',
  'even',
  'sandwich',
];

/** Pool categories: the admin overview mockup's (classic 9×9, the main families, mixed) plus
 * irregular and other sizes. */
export const CATEGORIES: readonly GeneratorCategory[] = [
  { key: 'classic', name: 'Classic 9×9', size: 9, irregular: false, types: [] },
  { key: 'thermo', name: 'Thermo', size: 9, irregular: false, types: ['thermo', 'slow-thermo'] },
  { key: 'killer', name: 'Killer', size: 9, irregular: false, types: ['killer'] },
  { key: 'kropki', name: 'Kropki', size: 9, irregular: false, types: ['difference', 'ratio'] },
  {
    key: 'renban-whispers',
    name: 'Renban and whispers',
    size: 9,
    irregular: false,
    types: ['renban', 'whispers'],
  },
  {
    key: 'mixed',
    name: 'Mixed variants',
    size: 9,
    irregular: false,
    types: MIXED_TYPES,
  },
  { key: 'irregular', name: 'Irregular 9×9', size: 9, irregular: true, types: [] },
  {
    key: 'six',
    name: '6×6 variants',
    size: 6,
    irregular: false,
    types: ['killer', 'thermo', 'arrow', 'renban', 'whispers', 'difference', 'ratio'],
  },
  { key: 'sixteen', name: 'Classic 16×16', size: 16, irregular: false, types: [] },
  { key: 'fog', name: 'Fog', size: 9, irregular: false, types: MIXED_TYPES, fog: true },
];

export function categoryOf(key: string): GeneratorCategory | undefined {
  return CATEGORIES.find((c) => c.key === key);
}

/** Difficulty choices on the Generate picker. */
export const BANDS = [
  { key: 'easy', name: 'Easy', min: 0, max: 3 },
  { key: 'medium', name: 'Medium', min: 3, max: 5 },
  { key: 'hard', name: 'Hard', min: 5, max: 7 },
  { key: 'expert', name: 'Expert', min: 7, max: 10 },
] as const;
export type BandKey = (typeof BANDS)[number]['key'];

/** What makes a generated puzzle feel hand-set, each 0–1 (core/src/generate/setter.rs). The
 * same shape holds the weights. */
export interface QualityFeatures {
  breakIn: number;
  noGuessing: number;
  variantWork: number;
  fewGivens: number;
  loadBearing: number;
  steadyCurve: number;
  visual: number;
}

export const DEFAULT_WEIGHTS: QualityFeatures = {
  breakIn: 1.0,
  noGuessing: 2.0,
  variantWork: 1.5,
  fewGivens: 1.0,
  loadBearing: 1.0,
  steadyCurve: 0.8,
  visual: 0.7,
};

export interface GeneratorSettings {
  /** Puzzles kept per category. */
  poolSize: number;
  /** Categories the pool keeps (and the Generate button offers). */
  categories: string[];
  /** The categories there were when these settings were saved: one added since starts on. */
  knownCategories: string[];
  /** Scores the pool generates, spread evenly over the bands inside. */
  minScore: number;
  maxScore: number;
  /** When the daily puzzle comes, HH:MM Belgrade time. */
  dailyTime: string;
  /** Share of days whose daily is a hand-set library puzzle (0–1). */
  handSetRatio: number;
  /** New dailies pause while everyone has this many unsolved. */
  backlog: number;
  /** The hardest score a classic daily may have (Marko, 2026-11-01: 3.9). */
  dailyMaxClassic: number;
  /** The hardest score a variant daily may have (10: no limit). */
  dailyMaxVariant: number;
  /** Share of the server's CPU cores the pool refill may use (0.1–1). */
  cpuLimit: number;
  /** No pool refills while paused (the Generate button still works). */
  paused: boolean;
  /** Quality weights, tuned from thumbs up and down on generated puzzles. */
  weights: QualityFeatures;
}

export const DEFAULT_GENERATOR: GeneratorSettings = {
  poolSize: 30,
  categories: CATEGORIES.map((c) => c.key),
  knownCategories: CATEGORIES.map((c) => c.key),
  minScore: 1,
  maxScore: 9,
  dailyTime: '06:00',
  handSetRatio: 0.2,
  backlog: 7,
  dailyMaxClassic: 3.9,
  dailyMaxVariant: 10,
  cpuLimit: 0.5,
  paused: false,
  weights: DEFAULT_WEIGHTS,
};

const num = (v: unknown, lo: number, hi: number, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback;

/** Stored settings over the defaults; invalid values fall back. */
export function normalizeGenerator(stored: unknown): GeneratorSettings {
  const s = (typeof stored === 'object' && stored !== null ? stored : {}) as Record<
    string,
    unknown
  >;
  const d = DEFAULT_GENERATOR;
  // Settings saved before this field knew every category but Fog.
  const known: unknown[] = Array.isArray(s.knownCategories)
    ? s.knownCategories
    : CATEGORIES.map((c) => c.key).filter((k) => k !== 'fog');
  const minScore = num(s.minScore, 0, 10, d.minScore);
  const weights = { ...DEFAULT_WEIGHTS };
  if (typeof s.weights === 'object' && s.weights !== null) {
    for (const k of Object.keys(weights) as (keyof QualityFeatures)[]) {
      weights[k] = num((s.weights as Record<string, unknown>)[k], 0, 5, weights[k]);
    }
  }
  return {
    poolSize: Math.round(num(s.poolSize, 0, 200, d.poolSize)),
    categories: Array.isArray(s.categories)
      ? CATEGORIES.map((c) => c.key).filter(
          (k) => (s.categories as unknown[]).includes(k) || !known.includes(k),
        )
      : d.categories,
    knownCategories: CATEGORIES.map((c) => c.key),
    minScore,
    maxScore: Math.max(minScore, num(s.maxScore, 0, 10, d.maxScore)),
    dailyTime:
      typeof s.dailyTime === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s.dailyTime)
        ? s.dailyTime
        : d.dailyTime,
    handSetRatio: num(s.handSetRatio, 0, 1, d.handSetRatio),
    backlog: Math.round(num(s.backlog, 1, 100, d.backlog)),
    dailyMaxClassic: num(s.dailyMaxClassic, 0, 10, d.dailyMaxClassic),
    dailyMaxVariant: num(s.dailyMaxVariant, 0, 10, d.dailyMaxVariant),
    cpuLimit: num(s.cpuLimit, 0.1, 1, d.cpuLimit),
    paused: typeof s.paused === 'boolean' ? s.paused : d.paused,
    weights,
  };
}

// ── Generate button ─────────────────────────────────────────────────────────

// ── The Generate picker: any size, any rules (Marko, 2026-11-02) ────────────────────────────

/** Sizes on the picker. */
export const GENERATE_SIZES = [4, 5, 6, 7, 8, 9, 12, 16] as const;

/** "a 6×6", "an 8×8". */
export function aGrid(size: number): string {
  return `${size === 8 ? 'an' : 'a'} ${size}×${size}`;
}

/** Sizes without rectangular boxes: their regions are always irregular. */
export function alwaysIrregular(size: number): boolean {
  return size === 5 || size === 7;
}

/** Irregular 16×16 grids mostly don't come back within the generator's time. */
export function neverIrregular(size: number): boolean {
  return size === 16;
}

/** Fog on a 16×16 mostly doesn't come back within the generator's time. */
export function neverFog(size: number): boolean {
  return size === 16;
}

/** The difficulties a choice can reach (measured: DECISIONS.md, Generate picker and Fog
 * generation). Plain classics top out sooner than puzzles with rules, and boxes sooner than
 * irregular regions. Fog stops at Hard for now (Marko, 2026-11-02): few reach Expert. */
export function bandsFor(
  r: Pick<GenerateRequest, 'size' | 'irregular' | 'rules' | 'globals' | 'surprise' | 'fog'>,
): readonly BandKey[] {
  const plain = !r.surprise && r.rules.length === 0 && r.globals.length === 0;
  const irregular = r.irregular || alwaysIrregular(r.size);
  let hardest: BandKey;
  if (r.fog) hardest = 'hard';
  else if (r.size === 4) hardest = 'easy';
  else if (!plain) hardest = r.size <= 6 ? 'hard' : 'expert';
  else if (irregular) hardest = r.size === 5 ? 'medium' : r.size <= 7 ? 'hard' : 'expert';
  else hardest = r.size === 6 ? 'easy' : r.size <= 9 ? 'hard' : 'expert';
  const keys = BANDS.map((b) => b.key);
  return keys.slice(0, keys.indexOf(hardest) + 1);
}

/** The choice with the regions its size allows and a difficulty it can reach. */
export function fitChoice(r: GenerateRequest): GenerateRequest {
  const irregular = alwaysIrregular(r.size) || (r.irregular && !neverIrregular(r.size));
  const fog = Boolean(r.fog) && !neverFog(r.size);
  // Fog needs clues to show as it clears: with no rules asked for, the generator picks some.
  const surprise = r.surprise || (fog && r.rules.length === 0);
  const most = maxRules(r.size);
  // The number can't be under the rules ticked or over what the size holds.
  const count =
    surprise && r.count != null && r.rules.length <= most
      ? Math.min(most, Math.max(r.count, r.rules.length))
      : null;
  const fitted = { ...r, irregular, fog, surprise, count };
  const bands = bandsFor(fitted);
  return { ...fitted, band: bands.includes(r.band) ? r.band : bands[bands.length - 1]! };
}

export interface GenerateRule {
  key: string;
  name: string;
  /** Constraint types it stands for: one rule, though kropki is white or black dots. */
  types: string[];
  /** One tap on the picker; the others are behind More rules. */
  common?: boolean;
}

/** Every rule the generator can place (the builder's "may add" list, tested to match). */
export const GENERATE_RULES: readonly GenerateRule[] = [
  { key: 'killer', name: 'Killer', types: ['killer'], common: true },
  { key: 'kropki', name: 'Kropki', types: ['difference', 'ratio'], common: true },
  { key: 'xv', name: 'XV', types: ['edge-sum'], common: true },
  { key: 'thermo', name: 'Thermo', types: ['thermo'], common: true },
  { key: 'arrow', name: 'Arrow', types: ['arrow'], common: true },
  { key: 'renban', name: 'Renban', types: ['renban'], common: true },
  { key: 'german-whispers', name: 'Whispers', types: ['whispers'], common: true },
  { key: 'odd-even', name: 'Odd/even', types: ['odd', 'even'], common: true },
  { key: 'palindrome', name: 'Palindrome', types: ['palindrome'] },
  { key: 'region-sum', name: 'Region sum', types: ['region-sum'] },
  { key: 'between', name: 'Between', types: ['between'] },
  { key: 'slow-thermo', name: 'Slow thermo', types: ['slow-thermo'] },
  { key: 'double-arrow', name: 'Double arrow', types: ['double-arrow'] },
  { key: 'nabner', name: 'Nabner', types: ['nabner'] },
  { key: 'lockout', name: 'Lockout', types: ['lockout'] },
  { key: 'entropic', name: 'Entropic', types: ['entropic'] },
  { key: 'modular', name: 'Modular', types: ['modular'] },
  { key: 'parity', name: 'Parity line', types: ['parity'] },
  { key: 'zipper', name: 'Zipper', types: ['zipper'] },
  { key: 'little-killer', name: 'Little killer', types: ['little-killer'] },
  { key: 'sandwich', name: 'Sandwich', types: ['sandwich'] },
  { key: 'x-sum', name: 'X-sums', types: ['x-sum'] },
  { key: 'skyscraper', name: 'Skyscrapers', types: ['skyscraper'] },
  { key: 'frame-sum', name: 'Frame sums', types: ['frame-sum'] },
  { key: 'battlefield', name: 'Battlefield', types: ['battlefield'] },
  { key: 'greater-than', name: 'Greater than', types: ['greater-than'] },
  { key: 'quadruple', name: 'Quadruple', types: ['quadruple'] },
  { key: 'extra-region', name: 'Extra region', types: ['extra-region'] },
  { key: 'min-max', name: 'Minimum / maximum', types: ['minimum', 'maximum'] },
  { key: 'fortress', name: 'Fortress', types: ['fortress'] },
  { key: 'clone', name: 'Clone', types: ['clone'] },
];

/** Rules for the whole grid. `diagonals` is both main diagonals. */
export const GENERATE_GLOBALS: readonly { key: string; name: string }[] = [
  { key: 'anti-knight', name: 'Anti-knight' },
  { key: 'anti-king', name: 'Anti-king' },
  { key: 'non-consecutive', name: 'Non-consecutive' },
  { key: 'diagonals', name: 'Diagonals' },
];

/** The rules a surprise picks from: outside clues only on a 9×9, where they have the room. */
export function mixRules(size: number): readonly string[] {
  const rules = ['killer', 'kropki', 'thermo', 'arrow', 'renban', 'german-whispers'];
  return size === 9 ? [...rules, 'palindrome', 'between', 'xv', 'odd-even', 'sandwich'] : rules;
}

/** The most rules the picker offers to pick on this size (measured: DECISIONS.md, Generate
 * picker). */
export function maxRules(size: number): number {
  return size <= 5 ? 2 : 5;
}

/** What the picker asks for. No rules: a classic. `surprise`: the generator adds rules of its own
 * to the ones ticked: a mix, or as many as make `count` in all. */
export interface GenerateRequest {
  size: number;
  irregular: boolean;
  rules: string[];
  globals: string[];
  surprise: boolean;
  /** With `surprise`: how many rules in all, every one in the puzzle. Null: whatever it picks. */
  count: number | null;
  /** A fog puzzle, solved through its fog. */
  fog: boolean;
  band: BandKey;
}

/** The same request written one way (so a waiting job can be found by it), or why it can't be
 * made. */
export function normalizeRequest(r: GenerateRequest): GenerateRequest | string {
  if (!r || !(GENERATE_SIZES as readonly number[]).includes(r.size)) return 'No such size.';
  const known = (keys: string[], list: readonly { key: string }[]) =>
    Array.isArray(keys) && keys.every((k) => list.some((x) => x.key === k));
  if (!known(r.rules, GENERATE_RULES) || !known(r.globals, GENERATE_GLOBALS)) {
    return 'No such rule.';
  }
  if (r.irregular && neverIrregular(r.size)) return 'This size doesn’t come irregular.';
  const fog = r.fog === true;
  if (fog && neverFog(r.size)) return 'This size doesn’t come with fog.';
  // Fog needs clues to show as it clears: with no rules asked for, the generator picks some.
  const surprise = r.surprise || (fog && r.rules.length === 0);
  const rules = [...new Set(r.rules)].sort();
  const count = surprise ? (r.count ?? null) : null;
  if (
    count !== null &&
    (!Number.isInteger(count) || count < 1 || count > maxRules(r.size) || count < rules.length)
  ) {
    return 'This grid doesn’t take that many rules.';
  }
  const clean: GenerateRequest = {
    size: r.size,
    irregular: r.irregular || alwaysIrregular(r.size),
    rules,
    globals: [...new Set(r.globals)].sort(),
    surprise,
    count,
    fog,
    band: r.band,
  };
  if (!bandsFor(clean).includes(r.band)) return 'This grid doesn’t reach that difficulty.';
  return clean;
}

/** What the generator gets for a request: the types it may place, the rules it must use. A
 * number of rules is picked here, afresh each time, so a try that doesn't fit can pick others. */
export function generationOf(
  r: GenerateRequest,
  random: () => number = Math.random,
): {
  types: string[];
  require: string[][];
  global: string[];
  fog: boolean;
} {
  const ruleOf = (k: string) => GENERATE_RULES.filter((x) => x.key === k);
  const ticked = r.rules.flatMap(ruleOf);
  const asked = {
    require: ticked.map((x) => x.types),
    global: r.globals,
    fog: r.fog,
  };
  if (!r.surprise) return { ...asked, types: ticked.flatMap((x) => x.types) };
  if (r.count === null) {
    // Today's mixes, and the rules ticked: those stay in the puzzle, the others may.
    const mix = (r.size === 9 ? categoryOf('mixed') : categoryOf('six'))?.types ?? [];
    return { ...asked, types: [...new Set([...ticked.flatMap((x) => x.types), ...mix])] };
  }
  const left = mixRules(r.size).filter((k) => !r.rules.includes(k));
  const picked: string[] = [];
  while (picked.length < r.count - r.rules.length && left.length > 0) {
    picked.push(left.splice(Math.floor(random() * left.length), 1)[0]!);
  }
  const all = [...ticked, ...picked.flatMap(ruleOf)];
  return { ...asked, types: all.flatMap((x) => x.types), require: all.map((x) => x.types) };
}

/** The picker's keys for a puzzle's constraint types. */
function keysOf(types: readonly string[]): string[] {
  const keys = types.map((t) => {
    if (t === 'diagonal') return 'diagonals';
    return GENERATE_RULES.find((x) => x.types.includes(t))?.key ?? t;
  });
  return [...new Set(keys)].sort();
}

/** A pool puzzle waiting to be served. */
export interface PoolPuzzle {
  category: string;
  size: number;
  /** The puzzle's constraint types. */
  types: string[];
  score: number;
}

/** Whether a pool puzzle is exactly what was asked for: the size, the regions and the rules. */
export function poolMatches(r: GenerateRequest, p: PoolPuzzle): boolean {
  const band = BANDS.find((b) => b.key === r.band)!;
  if (p.size !== r.size || p.score < band.min || p.score > band.max) return false;
  if ((p.category === 'fog') !== Boolean(r.fog)) return false;
  if (r.surprise) {
    if (r.irregular || r.globals.length > 0) return false;
    const has = keysOf(p.types);
    if (!r.rules.every((k) => has.includes(k))) return false;
    // A number of rules: any pool puzzle with that many will do.
    if (r.count !== null) return has.length === r.count;
    return (r.fog ? ['fog'] : ['mixed', 'six']).includes(p.category);
  }
  if ((p.category === 'irregular') !== r.irregular) return false;
  const asked = [...r.rules, ...r.globals].sort();
  const has = keysOf(p.types);
  return asked.length === has.length && asked.every((k, i) => k === has[i]);
}

/** What a puzzle made on demand is saved as (`titleFor` names it from its rules, if any). */
export function requestCategory(r: GenerateRequest): GeneratorCategory {
  const name = r.fog ? 'Fog' : r.irregular ? 'Irregular' : 'Classic';
  return {
    key: 'custom',
    name: `${name} ${r.size}×${r.size}`,
    size: r.size,
    irregular: r.irregular,
    types: generationOf(r).types,
    fog: r.fog,
  };
}

export interface GenerateOptions {
  /** Pool puzzles waiting to be served, for "Ready now". */
  pool: PoolPuzzle[];
}

/** A pool puzzle at once, or a job to wait for. */
export type GenerateResponse = { puzzleId: string } | { jobId: string };

export interface GenerateJobStatus {
  state: 'queued' | 'running' | 'done' | 'failed' | 'paused';
  progress: number;
  puzzleId: string | null;
  error: string | null;
}

// ── Admin ───────────────────────────────────────────────────────────────────

export interface PoolEntryDto {
  id: string;
  puzzleId: string;
  category: string;
  title: string;
  score: number | null;
  quality: number;
  createdAt: string;
  preview: PuzzleDoc;
}

export interface PoolResponse {
  categories: { key: string; name: string; count: number; size: number; enabled: boolean }[];
  entries: PoolEntryDto[];
  /** Generated puzzles served (opened from the pool) so far. */
  served: number;
}

export interface GeneratorAdminResponse {
  settings: GeneratorSettings;
  /** The refill job now, if one is queued or running. */
  refill: { state: string; progress: number; startedAt: string | null } | null;
  /** Ratings the weights were tuned from. */
  ratings: { up: number; down: number };
}

/** The Generator pool card on Admin → Overview (mockup 08). */
export interface PoolSummary {
  categories: { key: string; name: string; count: number; size: number }[];
  dailyTime: string;
  unplayed: number;
  backlog: number;
}
